import React from 'react';
import { Container, Row, Col, Button, Badge } from 'react-bootstrap';
import { CheckCircleFill, ArrowRightCircle } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';

interface Tier {
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  cta: string;
  ctaHref: string;
  ctaVariant: string;
  highlight?: boolean;
  badge?: string;
}

const TIERS: Tier[] = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Evaluate Arkodomo with a real proof-of-concept. Includes a 14-day trial of the headline three differentiators.',
    features: [
      '25 active identities',
      '1M token exchanges / month',
      'OPA policy enforcement',
      'MCP server integration',
      '7-day audit retention',
      '14-day trial: Delegation Chains & Intent-Bound Tokens',
      'Community support',
    ],
    cta: 'Start free',
    ctaHref: 'https://console.arkodomo.com',
    ctaVariant: 'outline-light',
  },
  {
    name: 'Team',
    price: '$2,500',
    period: '/ month',
    description: 'For platform teams shipping agentic workloads into production.',
    features: [
      'Up to 500 active identities',
      '25M token exchanges / month',
      'OPA policy-as-code',
      'Standard MCP integration',
      '90-day audit retention',
      'Email support',
      'SOC 2 reports on request',
    ],
    cta: 'Get started',
    ctaHref: 'https://console.arkodomo.com',
    ctaVariant: 'primary',
  },
  {
    name: 'Business',
    price: '$15,000',
    period: '/ month + usage',
    description: 'Unlimited scale with the full differentiating feature set and 24/7 support.',
    features: [
      'Unlimited active identities',
      '100M token exchanges / month included',
      'Provable Delegation chains',
      'Intent-Bound Tokens',
      '7-year audit retention',
      '24/7 support with named CSM',
      'Multi-region deployment',
    ],
    cta: 'Talk to us',
    ctaHref: '/contact',
    ctaVariant: 'primary',
    highlight: true,
    badge: 'Most popular',
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    description: 'Single-tenant deployment, custom data residency, and contractual SLAs for regulated industries.',
    features: [
      'Everything in Business',
      'Cryptographic Execution Receipts',
      'Single-tenant / VPC-isolated',
      'FedRAMP Moderate-aligned config',
      'Custom data residency (US, EU, GovCloud)',
      'Contractual SLA with credits',
      'Dedicated Technical Account Manager',
      'Annual contracts + multi-year discounts',
    ],
    cta: 'Contact sales',
    ctaHref: '/contact',
    ctaVariant: 'outline-light',
  },
];

const METERS = [
  {
    name: 'Active Workload Identity (AWI)',
    unit: 'identity / month',
    rate: '$2',
    note: 'Any workload that requested a token in the past 30 days.',
  },
  {
    name: 'Active Agent Identity (AAI)',
    unit: 'identity / month',
    rate: '$8–12',
    note: 'Volume-tiered. Agents consume the differentiating features — delegation, intent-binding, receipts — which justifies the premium.',
  },
  {
    name: 'Token exchanges',
    unit: 'per million',
    rate: '$5–10',
    note: 'Policy evaluations and token issuances above the included quota. Guardrail meter — not the primary revenue driver.',
  },
];

const AAI_TIERS = [
  { band: 'First 100 identities', rate: '$12 / identity', monthly: '$1,200' },
  { band: '101–500', rate: '$9 / identity', monthly: '+$3,600' },
  { band: '501–5,000', rate: '$6 / identity', monthly: '+$27,000' },
  { band: 'Beyond 5,000', rate: '$4 / identity', monthly: 'Negotiated' },
];

type Props = { onOpenWaitlist?: (reason?: 'request-access' | 'design-partner') => void };

export default function Pricing({ onOpenWaitlist }: Props) {
  return (
    <>
      {/* Hero */}
      <section className="py-5 text-light" style={{ paddingTop: '5rem' }}>
        <Container>
          <div className="text-center mb-5">
            <h1 className="display-5 fw-bold mb-3">Simple, transparent pricing</h1>
            <p className="text-white-50 mb-0" style={{ maxWidth: 560, margin: '0 auto', fontSize: '1.05rem' }}>
              A subscription floor that gates the differentiating features, plus consumption meters that scale with adoption.
              You pay more when you get more value — not before.
            </p>
          </div>

          {/* Tier cards */}
          <Row className="g-4 mb-5 align-items-stretch">
            {TIERS.map(tier => (
              <Col key={tier.name} xs={12} md={6} lg={3}>
                <div style={{
                  border: tier.highlight ? '1px solid rgba(246,196,83,0.5)' : '1px solid rgba(255,255,255,0.10)',
                  borderRadius: 12,
                  background: tier.highlight ? 'rgba(246,196,83,0.04)' : 'rgba(22,35,56,0.6)',
                  padding: '28px 22px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                }}>
                  {tier.badge && (
                    <div style={{
                      position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                      background: '#f6c453', color: '#0d0d0d', fontSize: 10, fontWeight: 700,
                      borderRadius: 4, padding: '2px 10px', letterSpacing: '0.06em', whiteSpace: 'nowrap',
                    }}>
                      {tier.badge.toUpperCase()}
                    </div>
                  )}

                  <div className="mb-1" style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{tier.name}</div>

                  <div className="mb-3">
                    <span style={{ fontSize: 32, fontWeight: 800, color: '#fff' }}>{tier.price}</span>
                    {tier.period && (
                      <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', marginLeft: 4 }}>{tier.period}</span>
                    )}
                  </div>

                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', marginBottom: 18, lineHeight: 1.55 }}>
                    {tier.description}
                  </p>

                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 24px', flex: 1 }}>
                    {tier.features.map(f => (
                      <li key={f} style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', marginBottom: 7, display: 'flex', alignItems: 'flex-start', gap: 7 }}>
                        <CheckCircleFill size={11} style={{ color: '#48c78e', flexShrink: 0, marginTop: 2 }} />
                        {f}
                      </li>
                    ))}
                  </ul>

                  {tier.ctaHref.startsWith('http') ? (
                    <a href={tier.ctaHref} className={`btn btn-${tier.ctaVariant} btn-sm w-100`} style={{ textDecoration: 'none' }}>
                      {tier.cta}
                    </a>
                  ) : (
                    <Button as={Link as any} to={tier.ctaHref} variant={tier.ctaVariant} size="sm" className="w-100">
                      {tier.cta}
                    </Button>
                  )}
                </div>
              </Col>
            ))}
          </Row>

          <p className="text-center text-white-50" style={{ fontSize: 12 }}>
            All plans billed monthly. Cancel anytime. Prices in USD. Enterprise requires annual contract.
          </p>
        </Container>
      </section>

      {/* Usage Meters */}
      <section className="py-5">
        <Container>
          <h2 className="h3 text-light mb-2 text-center">Usage-based meters</h2>
          <p className="text-white-50 text-center mb-5" style={{ fontSize: '0.92rem', maxWidth: 520, margin: '0 auto 2.5rem' }}>
            Above each tier's included quota, three meters run in parallel. You pay for what you use — not for identities that haven't touched your system in 30 days.
          </p>

          <Row className="g-4 mb-4">
            {METERS.map(m => (
              <Col key={m.name} md={4}>
                <div style={{ border: '1px solid rgba(255,255,255,0.09)', borderRadius: 10, padding: '22px 20px', background: 'rgba(22,35,56,0.5)', height: '100%' }}>
                  <div style={{ fontSize: 22, fontWeight: 800, color: '#f6c453', marginBottom: 4 }}>{m.rate}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{m.unit}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#fff', marginBottom: 8 }}>{m.name}</div>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', margin: 0, lineHeight: 1.55 }}>{m.note}</p>
                </div>
              </Col>
            ))}
          </Row>

          {/* AAI volume tiers */}
          <div style={{ border: '1px solid rgba(246,196,83,0.15)', borderRadius: 10, padding: '22px 24px', background: 'rgba(246,196,83,0.03)', maxWidth: 640, margin: '0 auto' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#f6c453', marginBottom: 14 }}>Agent Identity (AAI) volume tiers</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ color: 'rgba(255,255,255,0.35)', textAlign: 'left' }}>
                  <th style={{ paddingBottom: 8, fontWeight: 500 }}>Volume band</th>
                  <th style={{ paddingBottom: 8, fontWeight: 500 }}>Rate</th>
                  <th style={{ paddingBottom: 8, fontWeight: 500, textAlign: 'right' }}>Monthly cost</th>
                </tr>
              </thead>
              <tbody>
                {AAI_TIERS.map((row, i) => (
                  <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.7)' }}>
                    <td style={{ padding: '7px 0' }}>{row.band}</td>
                    <td>{row.rate}</td>
                    <td style={{ textAlign: 'right', color: '#f6c453', fontWeight: 600 }}>{row.monthly}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', margin: '12px 0 0' }}>
              Volume tiers prevent procurement sticker shock at scale. Sub-linear cost growth for high-density multi-agent pipelines.
            </p>
          </div>
        </Container>
      </section>

      {/* Free tier FAQ */}
      <section className="py-5">
        <Container style={{ maxWidth: 700 }}>
          <h2 className="h4 text-light mb-4 text-center">Frequently asked questions</h2>
          {[
            {
              q: 'What counts as an "active" identity?',
              a: 'Any workload or agent identity that requested a token in the past 30 days. Ephemeral CI runners, test agents, and decommissioned services drop off automatically — you\'re never charged for identities you\'re no longer using.',
            },
            {
              q: 'Why are agent identities priced higher than workload identities?',
              a: 'Agent identities consume the differentiating features — Delegation Chains and Intent-Bound Tokens (Business+), plus Cryptographic Execution Receipts (Enterprise) — that workloads do not. Per-identity cost to serve is genuinely higher, and the value created per agent identity is also higher.',
            },
            {
              q: 'What happens when I exceed my included quota?',
              a: 'You\'ll receive a soft alert at 75% of your included quota. Above the quota, the three usage meters run and are billed at the end of the billing period. We alert again at 200% to prevent bill shock.',
            },
            {
              q: 'What\'s included in the 14-day free trial?',
              a: 'Free tier accounts get a one-time 14-day trial of Delegation Chains and Intent-Bound Tokens. After 14 days, the trial auto-downgrades and these features become read-only so you can still see what was recorded — but not issue new delegated or intent-bound tokens.',
            },
            {
              q: 'Can I switch plans at any time?',
              a: 'Yes. Upgrades take effect immediately. Downgrades take effect at the end of your current billing period.',
            },
          ].map((item, i) => (
            <div key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', padding: '18px 0' }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 8 }}>{item.q}</div>
              <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)', margin: 0, lineHeight: 1.65 }}>{item.a}</p>
            </div>
          ))}
        </Container>
      </section>

      {/* CTA */}
      <section className="py-5">
        <Container>
          <div className="cta-gradient text-center">
            <h2 className="h3 text-light mb-3">Ready to get started?</h2>
            <p className="text-white-50 mb-4" style={{ maxWidth: 480, margin: '0 auto 1.5rem' }}>
              Start free, explore the platform, and upgrade when your team is ready to ship to production.
            </p>
            <div className="d-flex gap-3 justify-content-center flex-wrap">
              <a href="https://console.arkodomo.com" className="btn btn-primary btn-lg" style={{ textDecoration: 'none' }}>
                Start free <ArrowRightCircle className="ms-2" size={16} />
              </a>
              <Button variant="outline-light" size="lg" onClick={() => onOpenWaitlist && onOpenWaitlist('design-partner')}>
                Become a design partner
              </Button>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
